import { useMemo } from "react";
import { useSelector } from "react-redux";
import store from "redux/store";

// assets
import aircraftGroups from "assets/json/aircraftGroups.json";

const useModelData = () => {
	const aircraftList = useSelector(state => state.aircraftList);
	const allAircraft = useMemo(
		() =>
			Object.values(aircraftList.passenger).reduce((acc, manufacturer, i) => {
				return i === 0 ? acc : acc.concat(Object.values(manufacturer));
			}, []),
		[aircraftList]
	);

	const memorisedFunction = useMemo(() => modelId => allAircraft.find(aircraft => aircraft.id === modelId), [allAircraft]);

	return memorisedFunction;
};

const getModelData = modelId => {
	const aircraftList = store.getState().aircraftList;
	const allAircraft = Object.values(aircraftList.passenger).reduce((acc, manufacturer, i) => {
		return i === 0 ? acc : acc.concat(Object.values(manufacturer));
	}, []);

	return allAircraft.find(aircraft => aircraft.id === modelId);
};

/**
 * @param {string} modelId Aircraft model id.
 * @returns {string} Returns aircraft manufacturer name.
 */
function getAircraftManufacturer(modelId) {
	const aircraftList = store.getState().aircraftList;

	for (const manufacturer in aircraftList.passenger) {
		if (manufacturer === "manufactures") continue;
		if (aircraftList.passenger[manufacturer].find(a => a.id === modelId)) {
			return manufacturer;
		}
	}

	return null;
}

/**
 * Enum for aircraft sizes.
 * @readonly
 * @enum {Symbol}
 */
const AircraftSize = Object.freeze({
	SMALL: Symbol(0),
	MEDIUM: Symbol(1),
	BIG: Symbol(2),
	GIANT: Symbol(3),
	NONE: Symbol(4)
});

const AircraftSizeRanges = Object.freeze([
	{ id: AircraftSize.SMALL, min: 1, max: 60 },
	{ id: AircraftSize.MEDIUM, min: 61, max: 250 },
	{ id: AircraftSize.BIG, min: 251, max: 410 },
	{ id: AircraftSize.GIANT, min: 411, max: Infinity }
]);

const aircraftSizeMap = new Map([
	[AircraftSize.SMALL, ["small", "mały"]],
	[AircraftSize.MEDIUM, ["medium", "średni"]],
	[AircraftSize.BIG, ["big", "duży"]],
	[AircraftSize.GIANT, ["very big", "bardzo duży"]]
]);

/**
 * @return {Symbol} Returns AircraftSize enum
 */
function getAircraftSizeCategory(seatCount) {
	if (seatCount < 1) {
		throw new Error(`Invalid 'seatCount' ${seatCount}`);
	}

	seatCount = Math.round(seatCount);
	for (const range of AircraftSizeRanges) {
		if (seatCount >= range.min && seatCount <= range.max) {
			return range.id;
		}
	}

	throw new Error(`Invalid 'seatCount' (${seatCount}) provided for getAircraftSizeCategory()`);
}

/**
 * @return {string}
 */
function getAircraftSizeCategoryByType(id, language) {
	if (isNaN(+id)) {
		const modelData = getModelData(id);

		if (modelData == null) {
			throw new Error(`Unknown model id provided for getAircraftSizeCategoryByType. Got '${id}'`);
		}

		return aircraftSizeMap.get(getAircraftSizeCategory(modelData.passengers))[+(language === "PL")];
	}

	const group = aircraftGroups.modelGroups.find(gr => gr.id === +id);
	const average = group.models.reduce((acc, m) => (acc += getModelData(m).passengers), 0) / group.models.length;
	return aircraftSizeMap.get(getAircraftSizeCategory(average))[+(language === "PL")];
}

/**
 * @param {string} modelId Aircraft model id.
 * @return {Array<string | number, string>} group/model id and group/model name
 */
function getAircraftTypeGroup(modelId) {
	const group = aircraftGroups.modelGroups.find(gr => gr.models.includes(modelId));

	if (!group) {
		const model = getModelData(modelId);
		return [modelId, model.name];
	}

	return [group.id, group.name];
}

function getShorthandModelName(fullModelName) {
	fullModelName = fullModelName.replace("ATR ", "ATR");
	const splitted = fullModelName.split(" ");
	if (["Dreamliner"].includes(splitted[splitted.length - 1])) splitted.unshift();

	return splitted[splitted.length - 1].replace("ATR", "ATR ");
}

export default useModelData;
export {
	getModelData,
	useModelData,
	getAircraftSizeCategory,
	AircraftSize,
	getAircraftSizeCategoryByType,
	getAircraftTypeGroup,
	getAircraftManufacturer,
	getShorthandModelName
};
